import type {
  ImageField,
  KeyTextField,
  LinkField,
  RichTextField,
} from "@commercekit/prismic";
import { SliceZoneC } from "@commercekit/prismic";
import { asText } from "@prismicio/helpers";
import { PrismicImage, PrismicLink } from "@prismicio/react";
import { Fragment, type FC, useCallback, useEffect, useRef, useState } from "react";
import type {
  LayoutDocument,
  PaymentOptionsHorizontalSlice,
  CustomerReviewsBigSlice,
} from "~/lib/prismic";
import { CustomerReviewsBig, PaymentOptionsHorizontal } from "~/lib/prismic";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import { Disclosure } from "@headlessui/react";
import WvLink from "~/components/atoms/WvLink";
import { isUserUsingMobile } from "@commercekit/shared";
import { useLocalePath, useLocale } from "@commercekit/remix/lib/hooks/useLocale";
import { useLocation, useMatches } from "@remix-run/react";
import { MainReviewCard } from "~/components/reviews/MainReviewCard";
import { formatReviewDate } from "~/components/reviews/utils";
import { CustomerReviewCard } from "~/components/reviews/customer-reviews/CustomerReviewCard";

const FooterGeneral: FC<{
  contactTitle: KeyTextField;
  contact: RichTextField;
  footerLogo: ImageField;
  footerCustomerNavLinkTitle: KeyTextField;
  footerCustomerNavLink: LayoutDocument["data"]["footerCustomerNavLink"];
  footerTipsNavLinksTitle: KeyTextField;
  footerTipsNavLinks: LayoutDocument["data"]["footerTipsNavLinks"];
  footerMoreNavLinkTitle: KeyTextField;
  footerMoreNavLink: LayoutDocument["data"]["footerMoreNavLink"];
  footerCopyrightLinks: LayoutDocument["data"]["footerCopyrightLinks"];
  socialMedias: LayoutDocument["data"]["socialMedias"];
  logoLink: LinkField;
  footerPaymentOptionSlices: PaymentOptionsHorizontalSlice[];
  footerCustomerReviewSlices: CustomerReviewsBigSlice[];
  headerRating?: { rating: number; reviewCount: number } | null;
  brandImage?: ImageField;
  customerReviews?: {
    id: string;
    rating: number;
    comment: string;
    createdAt: string;
    submittedAt: string;
    verificationType: string;
    customer: { fullName: string };
  }[] | null;
}> = ({
  contactTitle,
  contact,
  footerLogo,
  socialMedias,
  footerCustomerNavLinkTitle,
  footerCustomerNavLink,
  footerTipsNavLinksTitle,
  footerTipsNavLinks,
  footerMoreNavLinkTitle,
  footerMoreNavLink,
  footerCopyrightLinks,
  logoLink,
  footerPaymentOptionSlices,
  footerCustomerReviewSlices,
  headerRating,
  brandImage,
  customerReviews,
}) => {
  const { $pt } = usePrismicLabels();
  const localePath = useLocalePath();
  const { localeFullName } = useLocale();
  const { pathname } = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const cardsRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    const isMobile = isUserUsingMobile();
    setIsMobile(isMobile);
  }, []);

  const updateScrollControls = useCallback(() => {
    const cardsElement = cardsRef.current;
    if (!cardsElement) {
      setCanScrollPrev(false);
      setCanScrollNext(false);
      return;
    }

    const maxScrollLeft =
      cardsElement.scrollWidth - cardsElement.clientWidth;

    setCanScrollPrev(cardsElement.scrollLeft > 4);
    setCanScrollNext(cardsElement.scrollLeft < maxScrollLeft - 4);
  }, []);

  useEffect(() => {
    const cardsElement = cardsRef.current;
    if (!cardsElement) {
      return;
    }

    cardsElement.scrollLeft = 0;
    const frameId = window.requestAnimationFrame(updateScrollControls);

    cardsElement.addEventListener("scroll", updateScrollControls, {
      passive: true,
    });
    window.addEventListener("resize", updateScrollControls);

    return () => {
      window.cancelAnimationFrame(frameId);
      cardsElement.removeEventListener("scroll", updateScrollControls);
      window.removeEventListener("resize", updateScrollControls);
    };
  }, [customerReviews, pathname, updateScrollControls]);

  const scrollCards = (direction: "prev" | "next") => {
    const cardsElement = cardsRef.current;
    if (!cardsElement) {
      return;
    }

    const firstCard = cardsElement.querySelector(
      ".footer-customer-reviews__card-wrapper"
    ) as HTMLDivElement | null;
    const cardWidth = firstCard?.offsetWidth || cardsElement.clientWidth;
    const nextLeft =
      direction === "next"
        ? cardsElement.scrollLeft + cardWidth + 16
        : cardsElement.scrollLeft - cardWidth - 16;

    cardsElement.scrollTo({
      left: nextLeft,
      behavior: "smooth",
    });
  };

  const isReviewsPage = pathname === localePath("/reviews") || pathname === localePath("/reviews/");
  const showETrustedSection = !isReviewsPage && headerRating && headerRating.rating > 0;

  // Custom content pages and blog post pages can override the reviews section
  // title via an optional `reviews_section_title_footer` Prismic field (Footer
  // tab). Falls back to the global label. The field is Rich Text, so it's
  // flattened to plain text.
  const matches = useMatches();
  const reviewsTitleOverride = matches
    .map((match) => {
      const data = match.data as
        | {
            result?: {
              customContentPage?: { data?: { reviews_section_title_footer?: RichTextField } };
              blogContent?: { data?: { reviews_section_title_footer?: RichTextField } };
            };
          }
        | undefined;
      const field =
        data?.result?.customContentPage?.data?.reviews_section_title_footer ??
        data?.result?.blogContent?.data?.reviews_section_title_footer;
      return field ? asText(field).trim() : "";
    })
    .filter(Boolean)
    .at(-1);

  const totalReviewsText = String(
    (headerRating?.reviewCount ?? 0).toLocaleString()
  );
  const reviewsSectionTitle = reviewsTitleOverride
    ? reviewsTitleOverride.replace(/{([^}]+)}/g, (ori, key) =>
        key === "totalReviews" ? totalReviewsText : ori
      )
    : $pt("reviews_page_title", { totalReviews: totalReviewsText });
  const reviewControls =
    customerReviews && customerReviews.length > 0 ? (
      <>
        <button
          type="button"
          className="footer-customer-reviews__control"
          aria-label="Show previous reviews"
          onClick={() => scrollCards("prev")}
          disabled={!canScrollPrev}
        >
          <span aria-hidden="true">‹</span>
        </button>
        <button
          type="button"
          className="footer-customer-reviews__control footer-customer-reviews__control--next"
          aria-label="Show next reviews"
          onClick={() => scrollCards("next")}
          disabled={!canScrollNext}
        >
          <span aria-hidden="true">›</span>
        </button>
      </>
    ) : null;

  return (
    <div className="footer">
      {showETrustedSection ? (
        <section className="footer-customer-reviews">
          <div className="container">
            <div className="py-4 py-lg-5">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="sans-serif strong mb-0 reviews_section_title">
                  {reviewsSectionTitle}
                </h2>
                <div className="footer-customer-reviews__controls d-none d-lg-flex align-items-center">
                  {reviewControls}
                </div>
              </div>
              <div className="d-flex flex-column flex-lg-row align-items-stretch">
              <div className="footer-customer-reviews__main-card mb-4 mb-lg-0 mr-lg-4">
                <MainReviewCard
                  rating={headerRating.rating}
                  totalReviews={headerRating.reviewCount}
                  brandImage={brandImage}
                  href="/reviews/"
                />
              </div>
              {customerReviews && customerReviews.length > 0 && (
                <div
                  ref={cardsRef}
                  className="footer-customer-reviews__cards flex-grow-1"
                >
                  <div className="d-flex flex-row footer-customer-reviews__scroll-container h-100">
                    {customerReviews.map((review) => (
                      <div key={review.id} className="footer-customer-reviews__card-wrapper flex-shrink-0">
                        <CustomerReviewCard
                          rating={review.rating}
                          comment={review.comment}
                          customerName={review.customer.fullName}
                          date={formatReviewDate(review.submittedAt || review.createdAt, localeFullName)}
                          verified={review.verificationType !== "none"}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </div>
              <div className="footer-customer-reviews__controls footer-customer-reviews__controls--mobile d-flex d-lg-none justify-content-end mt-3">
                {reviewControls}
              </div>
            </div>
          </div>
        </section>
      ) : (
        <SliceZoneC
          slices={footerCustomerReviewSlices}
          components={{ customer_reviews_big: CustomerReviewsBig }}
          defaultComponent={({ slice }) => <div>{slice.slice_type}</div>}
        />
      )}
      <SliceZoneC
        slices={footerPaymentOptionSlices}
        components={{ payment_options_horizontal: PaymentOptionsHorizontal }}
        defaultComponent={({ slice }) => <div>{slice.slice_type}</div>}
      />

      <div className="footer__menu-wrapper pb-4 pt-3 pt-lg-4 pt-xxl-5">
        <div className="container">
          <div className="row">
            {/* <div className="col-12 col-lg-8 col-xxl-3 order-lg-1">
              <div className="heading sans-serif normal sm inverted mb-4">
                {$pt("subscribe_to_newsletter_title")}
              </div>
              <div className="heading sans-serif normal sm inverted">
                {$pt("subscribe_to_newsletter_description")}
              </div>

              <form
                action=""
                method="post"
                name="mc-embedded-subscribe-form"
                id="mc-embedded-subscribe-form"
                className="form-inline"
                target="_blank"
              >
                <div className="form-row w-100">
                  <div className="col pr-0">
                    <input
                      type="email"
                      name="EMAIL"
                      className="form-control"
                      id="mce-EMAIL"
                      defaultValue=""
                      placeholder={$pt("subscribe_to_newsletter_placeholder")}
                    />
                  </div>
                  <div className="col-auto pl-0 ml-n1">
                    <button
                      type="submit"
                      name="subscribe"
                      id="mc-embedded-subscribe"
                      className="btn btn--medium btn--primary mb-2"
                    >
                      {$pt("subscribe_to_newsletter_button_text")}
                    </button>
                  </div>
                </div>
              </form>
            </div>*/}

            <div className="col-12 col-xxl-10">
              <div className="footer__menus my-3 mt-xxl-0" id="footer-menus">
                <div className="row">
                  {footerCustomerNavLink?.length ? (
                    <div className="col-12 col-lg-4 mb-3 footer__menu">
                      <Disclosure as="div" defaultOpen={!isMobile}>
                        {({ open }) => (
                          <>
                            <Disclosure.Button
                              as="div"
                              className={`${
                                !open ? "collapsed" : ""
                              } footer__menuhead heading mdl inverted strong`}
                            >
                              {footerCustomerNavLinkTitle}
                            </Disclosure.Button>
                            <div
                              className={`${
                                !open ? "collapse" : ""
                              } mb-3 mb-xxl-0`}
                            >
                              <Disclosure.Panel as="ul" static>
                                {footerCustomerNavLink?.map((link) => (
                                  <li key={asText(link.text)}>
                                    {link.use_absolute_url ? (
                                      <PrismicLink field={link.url}>
                                        {asText(link.text)}
                                      </PrismicLink>
                                    ) : (
                                      <WvLink to={localePath(`/${asText(link.path)}`)}>
                                        {asText(link.text)}
                                      </WvLink>
                                    )}
                                  </li>
                                ))}
                              </Disclosure.Panel>
                            </div>
                          </>
                        )}
                      </Disclosure>
                    </div>
                  ) : null}

                  {footerTipsNavLinks?.length ? (
                    <div className="col-12 col-lg-4 mb-3 footer__menu">
                      <Disclosure as="div" defaultOpen={!isMobile}>
                        {({ open }) => (
                          <>
                            <Disclosure.Button
                              as="div"
                              className={`${
                                !open ? "collapsed" : ""
                              } footer__menuhead heading mdl inverted strong`}
                            >
                              {footerTipsNavLinksTitle}
                            </Disclosure.Button>
                            <div
                              className={`${
                                !open ? "collapse" : ""
                              } mb-3 mb-xxl-0`}
                            >
                              <Disclosure.Panel as="ul" static>
                                {footerTipsNavLinks?.map((link) => (
                                  <li key={asText(link.text)}>
                                    {link.use_absolute_url ? (
                                      <PrismicLink field={link.url}>
                                        {asText(link.text)}
                                      </PrismicLink>
                                    ) : (
                                      <WvLink to={localePath(`/${asText(link.path)}`)}>
                                        {asText(link.text)}
                                      </WvLink>
                                    )}
                                  </li>
                                ))}
                              </Disclosure.Panel>
                            </div>
                          </>
                        )}
                      </Disclosure>
                    </div>
                  ) : null}
                  {footerMoreNavLink?.length ? (
                    <div className="col-12 col-lg-4 mb-3 footer__menu">
                      <Disclosure as="div" defaultOpen={!isMobile}>
                        {({ open }) => (
                          <>
                            <Disclosure.Button
                              as="div"
                              className={`${
                                !open ? "collapsed" : ""
                              } footer__menuhead heading mdl inverted strong`}
                            >
                              {footerMoreNavLinkTitle}
                            </Disclosure.Button>
                            <div
                              className={`${
                                !open ? "collapse" : ""
                              } mb-3 mb-xxl-0`}
                            >
                              <Disclosure.Panel as="ul" static>
                                {footerMoreNavLink?.map((link) => (
                                  <li key={asText(link.text)}>
                                    {link.use_absolute_url ? (
                                      <PrismicLink field={link.url}>
                                        {asText(link.text)}
                                      </PrismicLink>
                                    ) : (
                                      <WvLink to={localePath(`/${asText(link.path)}`)}>
                                        {asText(link.text)}
                                      </WvLink>
                                    )}
                                  </li>
                                ))}
                              </Disclosure.Panel>
                            </div>
                          </>
                        )}
                      </Disclosure>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-4 col-sm-6 col-xxl-2">
              <div className="footer__contact mb-3">
                <h3 className="heading mdl inverted strong"> {contactTitle}</h3>
                <div
                  className="footer__contact-text content inverted"
                  dangerouslySetInnerHTML={{ __html: asText(contact) }}
                ></div>
                <p> {$pt("follow_us_on_social_media_text")}:</p>
              </div>
              <div className="footer__socials mb-3 pb-5 pb-lg-0">
                {socialMedias &&
                  socialMedias?.map((social) => (
                    <Fragment key={social.name}>
                      <PrismicLink
                        field={social.link}
                        target={social.newWindow ? "_blank" : ""}
                        aria-label={social.name || ""}
                      >
                        <PrismicImage
                          alt=""
                          field={social.logo}
                          className="footer__socials--icon"
                          loading="lazy"
                        />
                      </PrismicLink>
                    </Fragment>
                  ))}
              </div>
            </div>

            <div className="footer__logo col-12 order-lg-2">
              <WvLink
                to={localePath("/")}
                className="d-block mt-3 pt-3 mb-0 mt-xxl-0 text-center"
              >
                <PrismicImage
                  alt=""
                  field={footerLogo}
                  loading="lazy"
                  width="203"
                  height="41"
                />
                <span className="footer__slogan d-none d-lg-block mt-2">
                  {$pt("header_slogan")}
                </span>
              </WvLink>
            </div>

            <div className="col-12 footer__menus order-lg-3">
              <div className="footer__menu copyrightbar">
                {footerCopyrightLinks?.length ? (
                  <ul className="d-flex flex-wrap justify-content-center">
                    {footerCopyrightLinks?.map((link) => (
                      <li key={asText(link.text)}>
                        {link.use_absolute_url ? (
                          <PrismicLink field={link.url}>
                            {asText(link.text)}
                          </PrismicLink>
                        ) : (
                          <WvLink to={localePath(`/${asText(link.path)}`)}>
                            {asText(link.text)}
                          </WvLink>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FooterGeneral;
